import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Range, getTrackBackground } from 'react-range';
import { withTheme } from 'styled-components';
import * as T from './TrackSlider.types';
import * as S from './TrackSlider.styles';
import * as C from './constants';
import * as A from 'modules/Player/actions';
import * as SL from 'modules/Player/selectors';
import { STOPED } from 'modules/Player/constants';
import { formatDuration } from 'components/PlayedDuration/helpers';

export class TrackSlider extends PureComponent<T.ITrackSliderProps, T.ITrackSliderState> {
  state = {
    durationPlayedInPermille: C.TRACK_SLIDER_DEFAULT_STATE_VALUE,
    isThumbHovered: false,
    // Used in getDerivedStateFromProps
    prevCurrentlyPlayingTrackId: null,
  };

  static getDerivedStateFromProps(nextProps: T.ITrackSliderProps, prevState: T.ITrackSliderState) {
    // This is here to ensure that if thumb is hovered and next track starts playing, its hover state should be reseted
    if (nextProps.currentlyPlayingTrackId !== prevState.prevCurrentlyPlayingTrackId) {
      return {
        prevCurrentlyPlayingTrackId: nextProps.currentlyPlayingTrackId,
        isThumbHovered: false,
      };
    }

    return null;
  }

  componentDidMount(): void {
    document.addEventListener('mouseup', this.onMouseUp);
  }

  componentWillUnmount(): void {
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  // TODO - disabled state based on playState & loaded status

  getSliderValues = () => {
    const { isSeeking } = this.props;

    return this.state.durationPlayedInPermille > C.TRACK_SLIDER_DEFAULT_STATE_VALUE && isSeeking
      ? [this.state.durationPlayedInPermille]
      : [this.props.durationPlayedInPermille];
  };

  getIsDisabled = () => {
    const { currentlyPlayingTrackId, soundRef, playStatus, currentlyPlayingTrackUrl } = this.props;

    return (
      !currentlyPlayingTrackId ||
      !soundRef.current ||
      !currentlyPlayingTrackUrl ||
      playStatus === STOPED ||
      soundRef.current.state() === 'loading'
    );
  };

  onMouseUp = () => {
    const { soundRef, dispatchSeeking, dispatchTrackProgress, isSeeking } = this.props;
    const isDisabled = this.getIsDisabled();

    if (!isSeeking || isDisabled) {
      return;
    }

    if (isSeeking && isDisabled) {
      dispatchSeeking({ isSeeking: false });

      return;
    }

    if (soundRef.current) {
      soundRef.current.seek(
        (soundRef.current.duration() / 1000) * this.state.durationPlayedInPermille,
      );

      const seek = soundRef.current.seek();
      if (
        typeof seek === 'number' &&
        this.state.durationPlayedInPermille > C.TRACK_SLIDER_DEFAULT_STATE_VALUE
      ) {
        dispatchTrackProgress({
          durationPlayedInPermille: this.state.durationPlayedInPermille,
          durationPlayedInSeconds: seek,
        });

        this.setState(() => ({
          durationPlayedInPermille: C.TRACK_SLIDER_DEFAULT_STATE_VALUE,
        }));
      }

      dispatchSeeking({ isSeeking: false });
    } else {
      console.error('TrackSlider.onSeekMouseUp() - soundRef.current not available');

      if (isSeeking) {
        dispatchSeeking({ isSeeking: false });
      }
    }
  };

  onChange = (values: number[]) => {
    this.setState((prevState) => {
      const newValue = values[0];
      if (prevState.durationPlayedInPermille === newValue) {
        return null;
      }

      return {
        durationPlayedInPermille: newValue,
      };
    });
  };

  onTrackTouchEnd = (event: React.TouchEvent) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    this.onMouseUp();
  };

  onThumbMouseEnter = () => {
    this.setState(() => ({
      isThumbHovered: true,
    }));
  };

  onThumbMouseLeave = () => {
    this.setState(() => ({
      isThumbHovered: false,
    }));
  };

  onThumbTouchEnd = (event: React.TouchEvent) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    this.onMouseUp();
  };

  renderTrack = (param: any) => {
    const { dispatchSeeking, theme, soundRef } = this.props;
    const isDisabled = this.getIsDisabled();

    return (
      <S.TrackWrapper
        className={`${isDisabled ? 'isDisabled' : ''}`}
        data-testid="TrackSlider-Track"
        onTouchStart={(event: React.TouchEvent) => {
          if (event.cancelable) {
            event.preventDefault();
          }

          if (isDisabled) {
            return;
          }

          if (soundRef.current && typeof soundRef.current.seek() === 'number') {
            dispatchSeeking({ isSeeking: true });
            param.props.onTouchStart(event);
          }
        }}
        onTouchEnd={this.onTrackTouchEnd}
        onMouseDown={(event: React.MouseEvent) => {
          if (isDisabled) {
            return;
          }

          if (soundRef.current && typeof soundRef.current.seek() === 'number') {
            dispatchSeeking({ isSeeking: true });
            param.props.onMouseDown(event);
          }
        }}
        style={param.props.style}
      >
        <S.TrackWrapperUnplayed
          {...param.props}
          style={{
            ...param.props.style,
            ...{
              background: getTrackBackground({
                values: this.getSliderValues(),
                colors: [theme.trackPlayedColor, theme.trackUnplayedColor],
                min: C.TRACK_SLIDER_MIN,
                max: C.TRACK_SLIDER_MAX,
              }),
            },
          }}
        >
          {param.children}
        </S.TrackWrapperUnplayed>
      </S.TrackWrapper>
    );
  };

  renderThumb = (param: any) => {
    const { durationPlayedInSeconds, trackDuration } = this.props;
    const { isThumbHovered } = this.state;
    const isDisabled = this.getIsDisabled();

    if (isDisabled) {
      return null;
    }

    return (
      <S.ThumbWrapper
        {...param.props}
        className={`${param.isDragged ? 'isDragged' : ''} ${isDisabled ? 'isDisabled' : ''}`}
        data-testid="TrackSlider-Thumb"
        style={param.props.style}
        onMouseEnter={this.onThumbMouseEnter}
        onMouseLeave={this.onThumbMouseLeave}
        onTouchEnd={this.onThumbTouchEnd}
      >
        {isThumbHovered && !isDisabled
          ? this.renderThumbIconHovered()
          : this.renderThumbIconDefault()}

        {(param.isDragged || isThumbHovered) && !isDisabled ? (
          <S.ThumbTooltip>
            {formatDuration({
              duration: param.isDragged
                ? Math.floor((trackDuration / 1000) * param.value)
                : durationPlayedInSeconds,
            })}
          </S.ThumbTooltip>
        ) : null}
      </S.ThumbWrapper>
    );
  };

  renderThumbIconDefault = () => {
    return (
      <svg
        id="thumbDefault"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 10 10"
      >
        <circle id="slider" className="cls-1" cx="5" cy="5" r="3.031" />
      </svg>
    );
  };

  renderThumbIconHovered = () => {
    return (
      <svg
        id="thumbHovered"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 10 10"
      >
        <path
          id="slider_actual"
          className="cls-1"
          d="M332.021,1361.01l-4-7h8.005Z"
          transform="translate(-327.031 -1352.5)"
        />
      </svg>
    );
  };

  render() {
    return (
      <React.Fragment>
        <Range
          disabled={this.getIsDisabled()}
          step={C.TRACK_SLIDER_STEP}
          min={C.TRACK_SLIDER_MIN}
          max={C.TRACK_SLIDER_MAX}
          values={this.getSliderValues()}
          onChange={this.onChange}
          renderTrack={this.renderTrack}
          renderThumb={this.renderThumb}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => ({
  currentlyPlayingTrackId: SL.getCurrentlyPlayingTrackId(state),
  trackDuration: SL.getCurrentlyPlayingTrackDuration(state),
  durationPlayedInPermille: SL.getDurationPlayedInPermille(state),
  durationPlayedInSeconds: SL.getDurationPlayedInSeconds(state),
  isSeeking: SL.getIsSeeking(state),
  playStatus: SL.getPlayStatus(state),
  currentlyPlayingTrackUrl: SL.getCurrentlyPlayingTrackUrl(state),
});

const mapDispatchToProps = {
  dispatchSeeking: A.seeking,
  dispatchTrackProgress: A.trackProgress,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(TrackSlider as any));
